import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

import { cn } from './cn';

type Variant = 'ghost' | 'secondary' | 'destructive';
type Size = 'sm' | 'md';

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  /** Required for accessibility — icon-only buttons need a label. */
  label: string;
  children: ReactNode;
}

const VARIANTS: Record<Variant, string> = {
  ghost: 'text-foreground-muted hover:bg-surface-muted hover:text-foreground',
  secondary:
    'bg-surface text-foreground ring-1 ring-inset ring-border-strong/70 shadow-e1 hover:bg-surface-muted hover:ring-border-strong',
  destructive: 'text-danger hover:bg-danger-soft hover:text-danger-foreground',
};

const SIZES: Record<Size, string> = {
  sm: 'size-7 [&_svg]:size-3.5',
  md: 'size-9 [&_svg]:size-4',
};

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { variant = 'ghost', size = 'md', label, className, children, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={label}
      className={cn(
        'inline-flex items-center justify-center rounded-md transition-[transform,background-color,box-shadow,color] duration-150 ease-out',
        'active:scale-95 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
        'disabled:cursor-not-allowed disabled:opacity-50 disabled:active:scale-100',
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
});
